import { forwardRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Check, Close, Edit } from "@mui/icons-material";
import { factory } from "../../../../../api/apiFactory";
import { parseSerials } from "../../../../../helpers/serials";
import { showNotification } from "../../../../../app/slices/notificationSlice";

const productApi = factory.get("product");

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// List of the serials of one product that one user currently holds; each can be edited inline.
const SerialsDialog = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, value }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    setEditing(null);
    setLoading(true);
    productApi
      .serials({ product_id: row.product_id, user_id: row.user_id })
      .then((res) => !cancelled && setSerials(parseSerials(res)))
      .catch(() => !cancelled && setSerials([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [row]);

  const saveSerial = () => {
    const value = editing.value.trim();
    if (!value) return;
    setSaving(true);
    productApi
      .updateSerial(editing.id, { serial_number: value })
      .then(() => {
        setSerials((prev) =>
          prev.map((s) =>
            s.id === editing.id ? { ...s, serial_number: value } : s,
          ),
        );
        setEditing(null);
        dispatch(showNotification({ message: "تم تعديل الرقم التسلسلي", type: "success" }));
      })
      .catch((err) =>
        dispatch(
          showNotification({
            message: err.response?.data?.errors?.[0] ?? "حدث خطأ",
            type: "error",
          }),
        ),
      )
      .finally(() => setSaving(false));
  };

  return (
    <Dialog
      open={Boolean(row)}
      TransitionComponent={Transition}
      fullWidth
      dir="rtl"
      onClose={onClose}
    >
      <DialogContent>
        <Typography variant="h4" color="purple" gutterBottom component="div">
          الارقام التسلسلية
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {`${row?.product_name ?? ""} - ${row?.user_name ?? ""}`}
        </Typography>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          {serials.length > 0 ? (
            serials.map((serial) =>
              editing?.id === serial.id ? (
                <Stack key={serial.id} direction="row" alignItems="center">
                  <TextField
                    size="small"
                    autoFocus
                    value={editing.value}
                    disabled={saving}
                    onChange={(e) =>
                      setEditing({ ...editing, value: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveSerial();
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        setEditing(null);
                      }
                    }}
                  />
                  <IconButton
                    size="small"
                    color="success"
                    disabled={saving || !editing.value.trim()}
                    onClick={saveSerial}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={saving}
                    onClick={() => setEditing(null)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Chip
                  key={serial.id}
                  label={serial.serial_number}
                  deleteIcon={<Edit />}
                  onDelete={() =>
                    setEditing({ id: serial.id, value: String(serial.serial_number ?? "") })
                  }
                />
              ),
            )
          ) : loading ? null : (
            <Typography variant="body2">لا توجد ارقام تسلسلية</Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SerialsDialog;
